import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import type { Stripe } from "@stripe/stripe-js";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { confirmSetupIntent, createSetupIntent } from "@/api/billing";
import { Button } from "@/components/ui/button";
import {
  paymentErrorMessage,
  stripeSetupErrorMessage,
} from "@/features/account-nux/lib/payment-errors";
import { getStripe } from "@/lib/stripe";

/** Collects a replacement card in Stripe Elements; owns an independent SetupIntent and never activates or charges the account. */
export function AutoRefillCardForm({
  orgId,
  onSaved,
  onCancel,
  onBusyChange,
}: {
  orgId: string;
  onSaved(): Promise<void>;
  onCancel(): void;
  onBusyChange(busy: boolean): void;
}) {
  const [formKey] = useState(() => crypto.randomUUID());
  const [stripe, setStripe] = useState<Stripe | null | undefined>();
  useEffect(() => {
    let active = true;
    void getStripe().then(
      (value) => {
        if (active) setStripe(value);
      },
      () => {
        if (active) setStripe(null);
      },
    );
    return () => {
      active = false;
    };
  }, []);
  const setup = useQuery({
    queryKey: ["billing", orgId, "auto-refill-card", formKey],
    queryFn: () => createSetupIntent(orgId, formKey),
    staleTime: Infinity,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });
  if (setup.isError || stripe === null)
    return (
      <div role="alert" className="space-y-2">
        <p>
          {setup.error
            ? paymentErrorMessage(setup.error)
            : "Card setup is unavailable. Please try again."}
        </p>
        <Button type="button" variant="outline" onClick={onCancel}>
          Back
        </Button>
        {setup.isError && (
          <Button type="button" onClick={() => void setup.refetch()}>
            Retry card setup
          </Button>
        )}
      </div>
    );
  if (!setup.data || stripe === undefined)
    return <p role="status">Loading secure card form…</p>;
  if (!["requires_payment_method", "succeeded"].includes(setup.data.status))
    return (
      <div className="space-y-2">
        <p role="status">
          Card setup is {setup.data.status}. Refresh its status or start again.
        </p>
        <Button type="button" variant="outline" onClick={onCancel}>
          Back
        </Button>
        <Button type="button" onClick={() => void setup.refetch()}>
          Check card setup
        </Button>
      </div>
    );
  return (
    <Elements
      stripe={stripe}
      options={{ clientSecret: setup.data.client_secret }}
    >
      <CardForm
        orgId={orgId}
        setupIntentId={setup.data.setup_intent_id}
        clientSecret={setup.data.client_secret}
        confirmed={setup.data.status === "succeeded"}
        onSaved={onSaved}
        onCancel={onCancel}
        onBusyChange={onBusyChange}
      />
    </Elements>
  );
}

function CardForm({
  orgId,
  setupIntentId,
  clientSecret,
  confirmed,
  onSaved,
  onCancel,
  onBusyChange,
}: {
  orgId: string;
  setupIntentId: string;
  clientSecret: string;
  confirmed: boolean;
  onSaved(): Promise<void>;
  onCancel(): void;
  onBusyChange(busy: boolean): void;
}) {
  const stripe = useStripe(),
    elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const confirmedRef = useRef(confirmed),
    needsReconcile = useRef(false),
    inFlight = useRef(false),
    active = useRef(true);
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);
  async function save() {
    if (!stripe || !elements || inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    onBusyChange(true);
    setError(null);
    try {
      if (!confirmedRef.current && needsReconcile.current) {
        // Stripe may have succeeded despite a lost response. Read the same intent
        // before another confirmation; never create a replacement for ambiguity.
        const result = await stripe.retrieveSetupIntent(clientSecret);
        if (!active.current) return;
        if (result.error) {
          setError(stripeSetupErrorMessage(result.error));
          return;
        }
        if (result.setupIntent?.id !== setupIntentId) {
          setError(
            "Could not verify your card setup. Close and reopen this form.",
          );
          return;
        }
        if (result.setupIntent.status === "succeeded")
          confirmedRef.current = true;
        else if (
          ![
            "requires_payment_method",
            "requires_confirmation",
            "requires_action",
          ].includes(result.setupIntent.status)
        ) {
          setError(
            "Stripe is still resolving your card setup. Try again to check its status.",
          );
          return;
        }
      }
      if (!confirmedRef.current) {
        needsReconcile.current = true;
        const result = await stripe.confirmSetup({
          elements,
          redirect: "if_required",
          confirmParams: { return_url: `${window.location.origin}/billing` },
        });
        if (!active.current) return;
        if (result.error) {
          setError(stripeSetupErrorMessage(result.error));
          return;
        }
        if (
          result.setupIntent?.id !== setupIntentId ||
          result.setupIntent.status !== "succeeded"
        ) {
          setError("Your card setup has not completed. Please try again.");
          return;
        }
        // A failed server response retries this intent, never asks Stripe to collect the card again.
        confirmedRef.current = true;
      }
      if (!active.current) return;
      await confirmSetupIntent(orgId, setupIntentId);
      if (active.current) await onSaved();
    } catch (cause) {
      if (active.current) setError(paymentErrorMessage(cause));
    } finally {
      inFlight.current = false;
      if (active.current) {
        setBusy(false);
        onBusyChange(false);
      }
    }
  }
  return (
    <div className="space-y-3">
      {!confirmedRef.current && <PaymentElement />}
      <p className="text-sm text-muted-foreground">
        Stripe securely saves this card as your default for future billing and
        auto refill. Saving a card does not enable auto refill.
      </p>
      {error && <p role="alert">{error}</p>}
      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          disabled={busy}
          onClick={onCancel}
        >
          Cancel card change
        </Button>
        <Button
          type="button"
          disabled={!stripe || !elements || busy}
          onClick={() => void save()}
        >
          {busy ? "Saving card…" : "Save card"}
        </Button>
      </div>
    </div>
  );
}
